import { Box, Text } from 'ink';
import { useEffect, useMemo, useState } from 'react';
import {
  BREAKPOINT_MAX_REMAINING_MS,
  type BreakpointConfig,
  type BreakpointPauseLease,
  type BreakpointRenewal,
  type Build,
  type CurrentBreakpointState,
  type HookBreakpointName,
  type HookBreakpointPosition,
  type IntrospectPlatformData,
  type PhaseBreakpointId,
  type PlatformName,
} from '@todesktop/shared';
import { formatBreakpoint } from '../utilities/introspectionBreakpoints';
import useInput from '../utilities/useInput';
import getCallableFirebaseFunction from '../utilities/getCallableFirebaseFunction';
import { currentUser } from '../utilities/firestore';
import { formatIntrospectionStatus } from '../utilities/introspectionStatus';

interface BreakpointPromptProps {
  appId?: string;
  build: Build;
}

function formatRemaining(remainingMs: number): string {
  if (Number.isNaN(remainingMs)) {
    return 'unknown';
  }

  if (remainingMs <= 0) {
    return 'expired';
  }

  const totalSeconds = Math.floor(remainingMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes <= 0) {
    return `${seconds}s`;
  }
  return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
}

function describeLeaseOwner(lease?: BreakpointPauseLease): string {
  if (!lease) {
    return 'Unknown';
  }
  return lease.createdByUserId;
}

function describeRenewals(renewals?: BreakpointRenewal[]): string {
  if (!renewals || renewals.length === 0) {
    return 'none';
  }
  return `${renewals.length}`;
}

export default function BreakpointPrompt({
  appId,
  build,
}: BreakpointPromptProps) {
  const [now, setNow] = useState(() => Date.now());
  const [error, setError] = useState<string | null>(null);
  const [inFlight, setInFlight] = useState<InFlightAction | null>(null);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const primary = useMemo<{
    platform: PlatformName;
    data: IntrospectPlatformData;
  } | null>(() => {
    const { introspect } = build;
    if (!introspect) return null;

    const pausedEntry = (
      Object.entries(introspect) as [PlatformName, IntrospectPlatformData][]
    ).find(([, data]) => data?.breakpointStatus === 'paused');

    if (!pausedEntry) {
      return null;
    }

    const [platform, data] = pausedEntry;
    return { platform, data };
  }, [build.introspect]);

  const isActive = Boolean(primary && !inFlight);

  useInput(
    async (input, key) => {
      if (!primary || !appId || inFlight) {
        return;
      }

      const lower = input.toLowerCase();
      const { platform } = primary;
      const actionLabel = (type: InFlightAction['action']) =>
        `${type} (${platform})`;

      async function invoke(action: 'resume' | 'renew' | 'skip') {
        setError(null);
        setInFlight({ platform, action });
        try {
          const user = currentUser();
          if (!user) {
            throw new Error('Not authenticated. Please run: todesktop login');
          }

          const idToken = await user.getIdToken(true);
          const actionMap: Record<
            typeof action,
            | 'resumeBuildBreakpoint'
            | 'renewBuildBreakpoint'
            | 'skipBuildBreakpoints'
          > = {
            resume: 'resumeBuildBreakpoint',
            renew: 'renewBuildBreakpoint',
            skip: 'skipBuildBreakpoints',
          };
          const functionName = actionMap[action];
          if (!functionName) {
            throw new Error(`Unknown breakpoint action: ${action}`);
          }

          const callFunction = getCallableFirebaseFunction(functionName);

          await callFunction({
            appId,
            buildId: build.id,
            platform,
            idToken,
          });
        } catch (err) {
          const message =
            err instanceof Error ? err.message : JSON.stringify(err);
          setError(`${actionLabel(action)} failed: ${message}`);
        } finally {
          setInFlight(null);
        }
      }

      if (key.return) {
        await invoke('resume');
      } else if (lower === 'x') {
        await invoke('skip');
      } else if (!key.ctrl && !key.meta && lower === ' ') {
        if (!canRenew) {
          return;
        }
        await invoke('renew');
      }
    },
    { isActive },
  );

  if (!primary) {
    return null;
  }

  const { platform, data } = primary;
  const breakpoint = data.currentBreakpoint;
  if (!breakpoint) {
    return null;
  }
  const { breakpointStatus } = data;
  const { shellStatus } = data;

  const expiresAtMs = toDateSafe(breakpoint.expiresAt)?.getTime() ?? NaN;
  const remainingMs = expiresAtMs - now;
  const canRenew = remainingMs < BREAKPOINT_MAX_REMAINING_MS;

  const formattedBreakpoint = formatBreakpoint(toBreakpointConfig(breakpoint));

  const renewalsDescription = describeRenewals(breakpoint.renewals);

  return (
    <Box flexDirection="column" marginBottom={1}>
      <Text color="yellow">
        {platform} {formatIntrospectionStatus(breakpointStatus, shellStatus)} at{' '}
        {formattedBreakpoint} (lease expires in {formatRemaining(remainingMs)}).
      </Text>
      <Text color="gray">
        Lease owner: {describeLeaseOwner(data.pauseLease)} · renewals:{' '}
        {renewalsDescription}
      </Text>
      <Text>
        Press <Text bold>Enter</Text> to continue, <Text bold>x</Text> to skip
        remaining breakpoints, <Text bold>space</Text> to renew lease
        {canRenew ? '' : ' (limit reached)'}, <Text bold>Ctrl+C</Text> to
        detach.
      </Text>
      {inFlight ? (
        <Text color="cyan">{getInFlightActionText(inFlight.action)}…</Text>
      ) : null}
      {error ? <Text color="red">{error}</Text> : null}
    </Box>
  );
}

function toDateSafe(value: unknown): Date | null {
  try {
    return toDate(value);
  } catch {
    return null;
  }
}

function toBreakpointConfig(state: CurrentBreakpointState): BreakpointConfig {
  if (state.type === 'hook') {
    return {
      type: 'hook',
      id: state.id as HookBreakpointName,
      position: (state.position ?? 'before') as HookBreakpointPosition,
    };
  }

  return {
    type: 'phase',
    id: state.id as PhaseBreakpointId,
  };
}

function getInFlightActionText(action: InFlightAction['action']): string {
  switch (action) {
    case 'renew':
      return 'Renewing lease';
    case 'skip':
      return 'Skipping remaining breakpoints';
    default:
      return 'Resuming build';
  }
}

function toDate(value: unknown): Date {
  if (!value) {
    throw new Error('Missing timestamp value');
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof value === 'string') {
    return new Date(value);
  }

  if (
    typeof value === 'object' &&
    (value as { toDate?: () => Date }).toDate instanceof Function
  ) {
    return (value as { toDate: () => Date }).toDate();
  }

  throw new Error('Unsupported timestamp value');
}

interface InFlightAction {
  action: 'resume' | 'renew' | 'skip';
  platform: PlatformName;
}
